/**
 * Hierarchy Database Report A (Contracted Agent) and Report B (Contracting Status).
 * Column headers match Paul LaMela's baseline spreadsheet layout.
 */
export const HIERARCHY_REPORT_IDENTITY_HEADERS = [
    "Agency Code",
    "Hierarchy Level",
    "Agent Name",
    "First Name",
    "Last Name",
    "Direct up line",
    "PHONE #",
    "EMAIL",
    "NPN",
    "Comp Level",
    "LOCATION",
];
export const HIERARCHY_REPORT_A_BUILDER_HEADER = "Builder";
export const REPORT_A_CARRIER_COLUMNS = [
    {
        slug: "american_amicable",
        writingHeader: "American Amicable Writing Code",
        commissionHeader: "American Amicable - Commission Level",
    },
    {
        slug: "occidental",
        writingHeader: "Occidental Writing Code",
        commissionHeader: "Occidental Commission Level",
    },
    {
        slug: "royal_neighbors",
        writingHeader: "Royal Neighbors Writing Number",
        commissionHeader: "Royal Neighbors Commission Level",
        reportingLevelHeader: "Royal Neighbors Reporting Level",
    },
    {
        slug: "corebridge",
        writingHeader: "Corebridge Writing Number",
        commissionHeader: "Corebridge Commission Level",
    },
    {
        slug: "transamerica",
        writingHeader: "Transamerica Writing Number",
        commissionHeader: "Transamerica Commission Level",
    },
    {
        slug: "american_home_life",
        writingHeader: "American Home Life Writing Number",
        commissionHeader: "American Home Life Commission Level",
    },
    {
        slug: "aflac",
        writingHeader: "Aflac Writing Number",
        commissionHeader: "Aflac Commission Level",
    },
    {
        slug: "americo",
        writingHeader: "Americo Writing Number",
        commissionHeader: "Americo Commission Level",
    },
    {
        slug: "mutual_of_omaha",
        writingHeader: "Mutual of Omaha Writing Number",
        commissionHeader: "Mutual of Omaha Commission Level",
    },
    {
        slug: "liberty_bankers",
        writingHeader: "Liberty Bankers Writing Number",
        commissionHeader: "Liberty Bankers Commission Level",
    },
];
export const REPORT_B_CARRIER_COLUMNS = [
    { slug: "baltimore_life", header: "Baltimore Life" },
    { slug: "illinois_mutual", header: "Illinois Mutual" },
    { slug: "f_and_g", header: "F&G" },
    { slug: "liberty_bankers", header: "Liberty Bankers" },
    { slug: "instabrain", header: "InstaBrain" },
    { slug: "foresters", header: "Foresters" },
    { slug: "kansas_city_life", header: "Kansas City Life" },
];
export const REPORT_A_HEADERS = [
    ...HIERARCHY_REPORT_IDENTITY_HEADERS,
    HIERARCHY_REPORT_A_BUILDER_HEADER,
    ...REPORT_A_CARRIER_COLUMNS.flatMap((carrier) => carrier.reportingLevelHeader
        ? [carrier.writingHeader, carrier.reportingLevelHeader, carrier.commissionHeader]
        : [carrier.writingHeader, carrier.commissionHeader]),
];
export const REPORT_B_HEADERS = [
    ...HIERARCHY_REPORT_IDENTITY_HEADERS,
    ...REPORT_B_CARRIER_COLUMNS.map((carrier) => carrier.header),
];
export const DEFAULT_AGENCY_CODE = "A0001";
export function defaultAgencyCode(override) {
    return override?.trim() || DEFAULT_AGENCY_CODE;
}
function stringField(formData, key) {
    const value = formData[key];
    if (typeof value === "string" && value.trim())
        return value.trim();
    return null;
}
export function resolveAgentFirstName(agent) {
    return agent.firstName ?? stringField(agent.formData, "firstName");
}
export function resolveAgentLastName(agent) {
    return agent.lastName ?? stringField(agent.formData, "lastName");
}
export function resolveAgentDisplayName(agent) {
    const first = resolveAgentFirstName(agent);
    const last = resolveAgentLastName(agent);
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (full)
        return full;
    return agent.email;
}
export function resolveAgentPhone(agent) {
    return stringField(agent.formData, "phone");
}
export function resolveAgentNpn(agent) {
    return agent.npn ?? stringField(agent.formData, "npn");
}
export function resolveAgentLocation(agent) {
    return (stringField(agent.formData, "city") ??
        stringField(agent.formData, "businessCity") ??
        stringField(agent.formData, "state"));
}
export function resolveAgentCompLevel(agent) {
    return agent.commissionLevel ?? stringField(agent.formData, "requestedCommissionLevel");
}
/** @interim Builder field pending product Q1; not persisted yet. */
export function resolveAgentBuilder(_agent) {
    return null;
}
/**
 * @interim Walk uplineUserId chain length from agent upward.
 * Returns "Missing" when the agent has no upline link.
 */
export function computeInterimHierarchyLevel(input) {
    if (!input.uplineUserId)
        return "Missing";
    const hops = input.uplineChainLength ?? 1;
    return hops + 1;
}
/** @interim Replace when Paul’s rules doc defines workflow status mapping. */
export function mapReportBStatus(contract) {
    const upstream = contract.upstreamStatusName?.trim();
    if (upstream)
        return upstream;
    const status = contract.status?.trim().toUpperCase();
    switch (status) {
        case "ACTIVE":
            return "Contracted";
        case "PENDING":
            return "Pending";
        case "REJECTED":
            return "Do Not Proceed";
        case "NIGO":
            return "Ready for Review";
        default:
            return null;
    }
}
export function contractByCarrier(contracts, slug) {
    return contracts.find((contract) => contract.carrier === slug);
}
export function buildReportAIdentityRow(agent, agencyCode) {
    return {
        "Agency Code": agencyCode,
        "Hierarchy Level": agent.hierarchyLevel,
        "Agent Name": resolveAgentDisplayName(agent),
        "First Name": resolveAgentFirstName(agent),
        "Last Name": resolveAgentLastName(agent),
        "Direct up line": agent.directUplineName,
        "PHONE #": resolveAgentPhone(agent),
        EMAIL: agent.email,
        NPN: resolveAgentNpn(agent),
        "Comp Level": resolveAgentCompLevel(agent),
        LOCATION: resolveAgentLocation(agent),
        Builder: resolveAgentBuilder(agent),
    };
}
export function buildReportARow(agent, agencyCode = defaultAgencyCode()) {
    const row = buildReportAIdentityRow(agent, agencyCode);
    for (const carrierDef of REPORT_A_CARRIER_COLUMNS) {
        const contract = contractByCarrier(agent.contracts, carrierDef.slug);
        row[carrierDef.writingHeader] = contract?.writingNumber ?? null;
        if (carrierDef.reportingLevelHeader) {
            row[carrierDef.reportingLevelHeader] = contract?.reportingLevel ?? null;
        }
        row[carrierDef.commissionHeader] =
            contract?.commissionLevel ?? resolveAgentCompLevel(agent);
    }
    return row;
}
export function buildReportBRow(agent, agencyCode = defaultAgencyCode()) {
    const row = {
        "Agency Code": agencyCode,
        "Hierarchy Level": agent.hierarchyLevel,
        "Agent Name": resolveAgentDisplayName(agent),
        "First Name": resolveAgentFirstName(agent),
        "Last Name": resolveAgentLastName(agent),
        "Direct up line": agent.directUplineName,
        "PHONE #": resolveAgentPhone(agent),
        EMAIL: agent.email,
        NPN: resolveAgentNpn(agent),
        "Comp Level": resolveAgentCompLevel(agent),
        LOCATION: resolveAgentLocation(agent),
    };
    for (const carrierDef of REPORT_B_CARRIER_COLUMNS) {
        const contract = contractByCarrier(agent.contracts, carrierDef.slug);
        row[carrierDef.header] = contract ? mapReportBStatus(contract) : null;
    }
    return row;
}
export function buildReportRows(kind, agents, agencyCode = defaultAgencyCode()) {
    const sorted = [...agents].sort((left, right) => resolveAgentDisplayName(left).localeCompare(resolveAgentDisplayName(right), undefined, {
        sensitivity: "base",
    }));
    if (kind === "contracted") {
        return sorted.map((agent) => buildReportARow(agent, agencyCode));
    }
    return sorted.map((agent) => buildReportBRow(agent, agencyCode));
}
export function reportHeaders(kind) {
    return kind === "contracted" ? REPORT_A_HEADERS : REPORT_B_HEADERS;
}
export function reportSheetName(kind) {
    return kind === "contracted"
        ? "Altura Contracted Agent Report "
        : "Altura Contracting Status Repor";
}
export function reportDownloadFilename(kind, date = new Date()) {
    const stamp = date.toISOString().slice(0, 10);
    return kind === "contracted"
        ? `Altura-Contracted-Agent-Report-${stamp}.xlsx`
        : `Altura-Contracting-Status-Report-${stamp}.xlsx`;
}
